import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Code2, ShieldCheck, Zap, Download, LayoutTemplate, FileCode, Boxes, Terminal, MessageCircle } from "lucide-react";
import { PublicLayout } from "../components/Common";
import { ProductCard, ProductCardSkeleton } from "../components/ProductCard";
import api from "../lib/api";
import { mediaUrl } from "../lib/format";

const TAGS = ["Laravel", "React", "Flutter", "Node.js", "PHP", "Tailwind CSS"];

function HeroTitle({ text }) {
  const words = String(text || "").trim().split(/\s+/);
  if (words.length <= 2) return <>{text}</>;
  return (
    <>
      {words.slice(0, -2).join(" ")} <span className="text-indigo-400">{words.slice(-2).join(" ")}</span>
    </>
  );
}
const TYPES = [
  { label: "Aplikasi", icon: Boxes, type: "APPLICATION", desc: "Aplikasi siap pakai" },
  { label: "Source Code", icon: FileCode, type: "SOURCE_CODE", desc: "Kode sumber lengkap" },
  { label: "Template", icon: LayoutTemplate, type: "TEMPLATE", desc: "Desain siap kustom" },
  { label: "Script", icon: Terminal, type: "SCRIPT", desc: "Otomasi & tools" },
];

const DEFAULT_HERO = {
  hero_title: "Temukan Aplikasi & Source Code Siap Pakai",
  hero_subtitle: "Solusi digital siap digunakan untuk bisnis, sekolah, UMKM, developer, dan kebutuhan lainnya.",
  contact_whatsapp: "",
  hero_animation: "fade-up",
  hero_video: "",
  hero_video_layout: "full",
};

const HERO_ANIMATION_CLASSES = {
  "fade-up": "hero-copy hero-fade-up",
  float: "hero-copy hero-float",
  glow: "hero-copy hero-glow",
  none: "hero-copy",
};

function whatsappAdminUrl(number) {
  let normalized = String(number || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
  if (normalized.startsWith("0")) normalized = `62${normalized.slice(1)}`;
  if (!normalized) return "";
  return `https://wa.me/${normalized}?text=${encodeURIComponent("Hallo Saya Sudah Mengajukan Pendaftaran sebagai Member di DS Kode")}`;
}

export default function Home() {
  const [products, setProducts] = useState(null);
  const [cats, setCats] = useState([]);
  const [banners, setBanners] = useState([]);
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "DS KODE - Marketplace Aplikasi & Source Code Siap Pakai";
    api.get("/products?sort=popular&limit=8").then((r) => setProducts(r.data.items)).catch(() => setProducts([]));
    api.get("/categories").then((r) => setCats(r.data.slice(0, 8))).catch(() => {});
    api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
    api.get("/settings").then((r) => setHero({
      hero_title: r.data.hero_title || DEFAULT_HERO.hero_title,
      hero_subtitle: r.data.hero_subtitle || DEFAULT_HERO.hero_subtitle,
      contact_whatsapp: r.data.contact_whatsapp || "",
      hero_animation: r.data.hero_animation || DEFAULT_HERO.hero_animation,
      hero_video: r.data.hero_video || "",
      hero_video_layout: r.data.hero_video_layout || "full",
    })).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(`/products?q=${encodeURIComponent(q)}`);
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className={`relative overflow-hidden bg-slate-900 ${hero.hero_video && hero.hero_video_layout === "right-half" ? "hero-video-split" : ""}`}>
        {hero.hero_video && (
          <video
            src={mediaUrl(hero.hero_video)}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            className={`absolute h-full object-cover ${hero.hero_video_layout === "right-half" ? "hero-video-right" : "inset-0 w-full opacity-30"}`}
          />
        )}
        {hero.hero_video && hero.hero_video_layout === "right-half" && <div className="hero-video-shade absolute inset-y-0 left-0 w-1/2" />}
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #4F46E5 0, transparent 40%), radial-gradient(circle at 80% 70%, #0EA5E9 0, transparent 40%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className={`${HERO_ANIMATION_CLASSES[hero.hero_animation] || HERO_ANIMATION_CLASSES[DEFAULT_HERO.hero_animation]} max-w-3xl`}>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-300 mb-4">
              <Code2 className="h-4 w-4" /> Marketplace Digital DS KODE
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
              <HeroTitle text={hero.hero_title} />
            </h1>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="hero-subtitle">
              {hero.hero_subtitle}
            </p>

            <form onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari aplikasi atau source code..."
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button type="submit" data-testid="hero-search-btn" className="px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors whitespace-nowrap">
                Cari Produk
              </button>
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-400">Populer:</span>
              {TAGS.map((t) => (
                <Link key={t} to={`/products?q=${t}`} className="text-xs font-mono px-2.5 py-1 rounded-full bg-white/10 text-slate-200 hover:bg-white/20 transition-colors">
                  {t}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/products" data-testid="hero-explore-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors">
                Jelajahi Produk <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/register" data-testid="hero-register-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors">
                Daftar Member
              </Link>
              {whatsappAdminUrl(hero.contact_whatsapp) && (
                <a href={whatsappAdminUrl(hero.contact_whatsapp)} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors">
                  <MessageCircle className="h-4 w-4" /> WhatsApp Admin
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, title: "Aman & Terpercaya", desc: "Download tersproteksi khusus member." },
            { icon: Zap, title: "Cepat & Ringan", desc: "Akses katalog super cepat & responsif." },
            { icon: Download, title: "Siap Pakai", desc: "Dokumentasi lengkap & update gratis." },
          ].map((f) => (
            <div key={f.title} className="bg-white rounded-xl border border-slate-200/80 p-5 shadow-sm flex items-start gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600 shrink-0"><f.icon className="h-5 w-5" /></span>
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-sm">{f.title}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Product types */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Jenis Produk</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {TYPES.map((t) => (
            <Link key={t.type} to={`/products?type=${t.type}`} data-testid={`type-${t.type}`} className="group bg-white rounded-xl border border-slate-200/80 p-5 hover:border-indigo-300 hover:shadow-lg transition-all">
              <span className="grid place-items-center h-11 w-11 rounded-lg bg-indigo-50 text-indigo-600 mb-3 group-hover:bg-indigo-600 group-hover:text-white transition-colors"><t.icon className="h-5 w-5" /></span>
              <h3 className="font-heading font-semibold text-slate-900">{t.label}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Produk Populer</h2>
          <Link to="/products" className="text-sm font-semibold text-indigo-600 flex items-center gap-1 hover:gap-2 transition-all">Lihat Semua <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products === null
            ? Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={i} />)
            : products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* Banners */}
      {banners.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="home-banners">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {banners.map((b) => (
              <Link
                key={b.id}
                to={b.link || "/products"}
                data-testid={`banner-${b.id}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200/80 min-h-[180px] flex items-end"
              >
                {b.image && (
                  <img src={mediaUrl(b.image)} alt={b.title} loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
                <div className="relative p-6 w-full">
                  <h3 className="font-heading text-xl font-bold text-white">{b.title}</h3>
                  {b.subtitle && <p className="text-sm text-slate-200 mt-1">{b.subtitle}</p>}
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 px-3 py-1.5 rounded-full group-hover:gap-2.5 transition-all">
                    Lihat Selengkapnya <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      {cats.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Kategori Populer</h2>
          <div className="flex flex-wrap gap-3">
            {cats.map((c) => (
              <Link key={c.id} to={`/products?category_id=${c.id}`} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:border-indigo-300 hover:text-indigo-600 transition-colors">
                {c.name} <span className="text-slate-400">({c.product_count})</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
